import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { X, Send, Check } from "lucide-react";
import PhoneInput, {
  type Country,
  getCountryCallingCode,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import PhoneCountrySelectDrawer from "../../components/auth/PhoneCountrySelectDrawer";
import Loader from "../../components/common/Loader";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const PHONE_MAX_LENGTHS: Partial<Record<string, number>> = {
  IN: 10,
  PK: 10,
  BD: 10,
  LK: 9,
  NP: 10,
  AF: 9,
  MV: 7,
  CN: 11,
  JP: 10,
  KR: 10,
  TW: 9,
  HK: 8,
  MO: 8,
  ID: 12,
  PH: 10,
  VN: 9,
  TH: 9,
  MY: 9,
  SG: 8,
  MM: 9,
  KH: 9,
  LA: 9,
  BN: 7,
  AE: 9,
  SA: 9,
  QA: 8,
  KW: 8,
  BH: 8,
  OM: 8,
  JO: 9,
  IL: 9,
  TR: 10,
  IR: 10,
  IQ: 10,
  YE: 9,
  SY: 9,
  LB: 8,
  NG: 10,
  ZA: 9,
  EG: 10,
  KE: 9,
  GH: 9,
  ET: 9,
  TZ: 9,
  UG: 9,
  TN: 8,
  MA: 9,
  DZ: 9,
  SD: 9,
  MZ: 9,
  AO: 9,
  CM: 9,
  CI: 8,
  SN: 9,
  ZW: 9,
  GB: 10,
  DE: 10,
  FR: 9,
  IT: 10,
  ES: 9,
  NL: 9,
  BE: 9,
  SE: 9,
  NO: 8,
  DK: 8,
  FI: 9,
  PL: 9,
  RU: 10,
  UA: 9,
  PT: 9,
  GR: 10,
  AT: 10,
  CH: 9,
  CZ: 9,
  HU: 9,
  RO: 9,
  BG: 9,
  HR: 9,
  SK: 9,
  LT: 8,
  LV: 8,
  EE: 8,
  US: 10,
  CA: 10,
  MX: 10,
  BR: 11,
  AR: 10,
  CO: 10,
  PE: 9,
  CL: 9,
  VE: 10,
  EC: 9,
  BO: 8,
  PY: 9,
  UY: 8,
  AU: 9,
  NZ: 8,
};

const getMaxNationalLength = (country: Country): number => {
  if (PHONE_MAX_LENGTHS[country] !== undefined) {
    return PHONE_MAX_LENGTHS[country]!;
  }
  try {
    const callingCode = getCountryCallingCode(country);
    let maxFound = 10;
    for (let len = 3; len <= 15; len++) {
      const phone = parsePhoneNumberFromString(
        `+${callingCode}${"9".repeat(len)}`,
        country,
      );
      if (phone && phone.isPossible() && phone.nationalNumber.length === len) {
        maxFound = len;
      }
    }
    return maxFound;
  } catch {
    return 10;
  }
};

type BookDemoModalProps = {
  open: boolean;
  onClose: () => void;
};

type ViewState = "form" | "loading" | "success";

const BookDemoModal = ({ open, onClose }: BookDemoModalProps) => {
  const { stores, currentShop } = useAuth();
  const [view, setView] = useState<ViewState>("form");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country>("IN");
  const [formError, setFormError] = useState<string | null>(null);
  const [storeUrl, setStoreUrl] = useState("");
  const [storeUrlError, setStoreUrlError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  // Autofill store URL from user's shop data
  useEffect(() => {
    if (open && (stores.length > 0 || currentShop)) {
      const currentStore =
        stores.find((s) => s.shop_domain === currentShop) || stores[0];
      if (currentStore?.shop_domain) {
        // Remove https://, http://, .myshopify.com for display
        const shopValue = currentStore.shop_domain
          .replace(/^https?:\/\//i, "")
          .replace(/\.myshopify\.com$/i, "");
        setStoreUrl(shopValue);
      }
    }
  }, [open, stores, currentShop]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setView("form");
      setName("");
      setNameError(null);
      setEmail("");
      setEmailError(null);
      setPhoneNumber(undefined);
      setPhoneError(null);
      setFormError(null);
      setStoreUrl("");
      setStoreUrlError(null);
    }, 300);
  };

  // Email validation
  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address.";
    return null;
  };

  const handleEmailBlur = () => {
    setEmailError(validateEmail(email));
  };

  // Phone handlers — same logic as SignUpForm
  const handlePhoneChange = (value?: string) => {
    const country = (selectedCountry || "IN") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;

    if (value && !value.startsWith("+") && /^\d+$/.test(value)) {
      const cleanedDigits = value.replace(/\D/g, "");
      if (cleanedDigits.length > 0) {
        if (country === "IN" && cleanedDigits.length === 10) {
          setPhoneNumber(`${prefix}${cleanedDigits}`);
          setPhoneError(null);
          return;
        } else if (cleanedDigits.length <= getMaxNationalLength(country)) {
          setPhoneNumber(`${prefix}${cleanedDigits}`);
          setPhoneError(null);
          return;
        }
      }
    }

    if (!value || !value.startsWith("+")) {
      setPhoneNumber(prefix);
      setPhoneError(null);
      return;
    }

    const maxLen = getMaxNationalLength(country);
    const nationalDigits = value.startsWith(prefix)
      ? value.slice(prefix.length).replace(/\D/g, "")
      : value.replace(/^\+\d+/, "").replace(/\D/g, "");

    if (nationalDigits.length > maxLen) return;

    const phone = parsePhoneNumberFromString(value, country);
    if (phone?.country) {
      setSelectedCountry(phone.country as Country);
    }

    if (nationalDigits.length === maxLen) {
      setPhoneError(
        phone && phone.isValid()
          ? null
          : "Invalid phone number for the selected country.",
      );
    } else {
      setPhoneError(null);
    }

    setPhoneNumber(value);
  };

  const handlePhoneBlur = () => {
    const country = (selectedCountry || "IN") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;

    if (!phoneNumber || phoneNumber === prefix) {
      setPhoneError("Phone number is required.");
      return;
    }

    const maxLen = getMaxNationalLength(country);
    const nationalDigits = phoneNumber.startsWith(prefix)
      ? phoneNumber.slice(prefix.length).replace(/\D/g, "")
      : phoneNumber.replace(/^\+\d+/, "").replace(/\D/g, "");

    if (nationalDigits.length < maxLen) {
      setPhoneError(
        `Phone number must be ${maxLen} digits for the selected country.`,
      );
      return;
    }

    const phone = parsePhoneNumberFromString(phoneNumber, country);
    if (!phone || !phone.isValid()) {
      setPhoneError("Invalid phone number for the selected country.");
      return;
    }

    setPhoneError(null);
  };

  const validateStoreUrl = (value: string): string | null => {
  if (!value.trim()) return "Store URL is required.";

  const cleaned = value.trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");

  if (cleaned.includes(".")) {
    // dot ???? minimum 2 characters
    const parts = cleaned.split(".");
    const afterDot = parts.slice(1).join(".");
    if (afterDot.length < 2) {
      return "Please enter a valid store URL (e.g. yourstore.com).";
    }
    return null;
  }

  // ???? store name (no dot) — minimum 2 characters
  if (cleaned.length < 2) {
    return "Store name must be at least 2 characters.";
  }

  return null;
};

  const handleStoreUrlBlur = () => {
    setStoreUrlError(validateStoreUrl(storeUrl));
  };

  const cleaned = storeUrl.trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");

  const handleSubmit = async () => {
    // Validate all fields
    const nameErr = !name.trim() ? "Name is required." : null;
    setNameError(nameErr);

    const emailErr = validateEmail(email);
    setEmailError(emailErr);

    const country = (selectedCountry || "IN") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;
    const nationalDigits = phoneNumber
      ? phoneNumber.startsWith(prefix)
        ? phoneNumber.slice(prefix.length).replace(/\D/g, "")
        : phoneNumber.replace(/^\+\d+/, "").replace(/\D/g, "")
      : "";
    const maxLen = getMaxNationalLength(country);

    const storeUrlErr = validateStoreUrl(storeUrl);
    setStoreUrlError(storeUrlErr);

    if (!phoneNumber || phoneNumber === prefix || nationalDigits.length === 0) {
      setPhoneError("Phone number is required.");
    } else if (nationalDigits.length < maxLen) {
      setPhoneError(
        `Phone number must be ${maxLen} digits for the selected country.`,
      );
    } else {
      const parsed = parsePhoneNumberFromString(phoneNumber, country);
      if (!parsed || !parsed.isValid()) {
        setPhoneError("Invalid phone number for the selected country.");
      } else {
        setPhoneError(null);
      }
    }

    if (
      nameErr ||
      emailErr ||
      phoneError ||
      !phoneNumber ||
      storeUrlErr ||
      phoneNumber === prefix
    )
      return;

    setFormError(null);
    setView("loading");

    // try {
    //   const response = await fetch("/api/demo-request", {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //       Accept: "application/json",
    //     },
    //     body: JSON.stringify({
    //       name: name.trim(),
    //       email: email.trim(),
    //       phone: phoneNumber,
    //       shop_url: storeUrl.trim(),
    //     }),
    //   });

    //   const data = await response.json();

    //   if (!response.ok || !data.success) {
    //     setFormError(data.message || "Something went wrong. Please try again.");
    //     setView("form");
    //     return;
    //   }

    //   setView("success");
    // } catch (err) {
    //   setFormError("Network error. Please check your connection.");
    //   setView("form");
    // }
    try {
      const response = await axios.post(
        "/demo-request",
        {
          name: (() => {
            const first = name.trim().split(" ")[0];
            return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
          })(),
          email: email.trim(),
          phone: phoneNumber,
          shop_url: `https://${cleaned}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const data = response.data;

      if (!data.success) {
        setFormError(data.message || "Something went wrong. Please try again.");
        setView("form");
        return;
      }

      setView("success");
    } catch (err: any) {
      console.error(err);

      setFormError(
        err.response?.data?.message ||
          "Network error. Please check your connection.",
      );

      setView("form");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black opacity-50 flex items-center justify-center z-[99999] px-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white dark:bg-[var(--card)] rounded-xl border border-gray-200 dark:border-[#FFFFFF0F] shadow-xl w-full max-w-md"
          >
            {/* Header */}
            {view !== "success" && (
              <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-gray-200 dark:border-[#FFFFFF0F]">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    Watch Demo
                  </h2>
                  {/* <p className="text-[12px] text-[var(--mute)] mt-0.5">
                    Our team will reach out within 24 hours
                  </p> */}
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 mt-0.5"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className="px-6 py-5">
              <AnimatePresence mode="wait">
                {/* Form */}
                {view === "form" && (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Form level error */}
                    {formError && (
                      <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                        {formError}
                      </div>
                    )}

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          const val = e.target.value.replace(
                            /[^A-Za-z\s]/g,
                            "",
                          );
                          setName(val);
                          if (nameError) setNameError(null);
                        }}
                        onBlur={() => {
                          if (!name.trim()) setNameError("Name is required.");
                        }}
                        placeholder="Your full name"
                        className={`w-full h-11 px-3 text-sm rounded-[6px] border bg-transparent dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-[var(--accent)] focus:ring-[var(--accent)] ${
                          nameError
                            ? "border-red-400 dark:border-red-500"
                            : "border-gray-300 dark:border-gray-700"
                        }`}
                      />
                      {nameError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {nameError}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(null);
                        }}
                        onBlur={handleEmailBlur}
                        placeholder="you@company.com"
                        className={`w-full h-11 px-3 text-sm rounded-[6px] border bg-transparent dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-[var(--accent)] focus:ring-[var(--accent)] ${
                          emailError
                            ? "border-red-400 dark:border-red-500"
                            : "border-gray-300 dark:border-gray-700"
                        }`}
                      />
                      {emailError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {emailError}
                        </p>
                      )}
                    </div>

                    {/* WhatsApp / Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Phone number <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        countryCallingCodeEditable={false}
                        international
                        countrySelectComponent={PhoneCountrySelectDrawer}
                        placeholder="Enter your phone number"
                        value={
                          phoneNumber ||
                          `+${getCountryCallingCode(selectedCountry || "IN")}`
                        }
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        defaultCountry={selectedCountry}
                        onCountryChange={(c) => {
                          const nextCountry = (c || "IN") as Country;
                          setSelectedCountry(nextCountry);
                          const callingCode =
                            getCountryCallingCode(nextCountry);
                          setPhoneNumber(`+${callingCode}`);
                          setPhoneError(null);
                        }}
                        className={`phone-input-container h-11 border rounded-lg px-0.5 bg-white dark:bg-gray-900 hover:border-[var(--accent)] focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--accent)] transition-colors [&_.PhoneInputCountry]:mr-0 [&_.PhoneInputCountry]:pl-0 [&_.PhoneInputCountry]:pr-0 [&_.PhoneInputInput]:pl-0 ${
                          phoneError
                            ? "border-red-400 dark:border-red-500"
                            : "border-gray-300 dark:border-gray-700"
                        }`}
                        numberInputProps={{
                          className:
                            "h-11 w-full bg-transparent rounded-r-lg text-sm border-0 ring-0 shadow-none outline-none focus:outline-none focus:ring-0 dark:text-white/90",
                          onKeyDown: (
                            e: React.KeyboardEvent<HTMLInputElement>,
                          ) => {
                            if (!/^\d$/.test(e.key)) return;
                            const country = (selectedCountry ||
                              "IN") as Country;
                            const cc = getCountryCallingCode(country);
                            const maxLen = getMaxNationalLength(country);
                            const totalDigits = e.currentTarget.value.replace(
                              /\D/g,
                              "",
                            );
                            const nationalDigits = totalDigits.slice(cc.length);
                            if (nationalDigits.length >= maxLen) {
                              e.preventDefault();
                            }
                          },
                        }}
                        countrySelectProps={{
                          className:
                            "h-11 rounded-l-lg bg-transparent px-0 border-0 ring-0 shadow-none outline-none dark:text-white/90",
                        }}
                      />
                      {phoneError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {phoneError}
                        </p>
                      )}
                    </div>

                    {/* Store URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Store URL <span className="text-red-500">*</span>
                      </label>
                      <div
                        className={`flex items-center h-11 rounded-[6px] border bg-transparent dark:bg-gray-900 focus-within:ring-2 focus-within:border-[var(--accent)] focus-within:ring-[var(--accent)] overflow-hidden ${storeUrlError ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-700"}`}
                      >
                        <span className="ml-3 -mr-2 text-sm text-gray-400 dark:text-gray-500 select-none whitespace-nowrap">
                          https://
                        </span>
                        <input
                          type="text"
                          value={storeUrl}
                          onChange={(e) => {
                            const val = e.target.value.replace(
                              /^https?:\/\//i,
                              "",
                            );
                            setStoreUrl(val);
                            if (storeUrlError) setStoreUrlError(null);
                          }}
                          onBlur={handleStoreUrlBlur}
                          placeholder="yourstore.com"
                          className="flex-1 h-full text-sm bg-transparent border-0 outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 pr-3"
                        />
                      </div>
                      {storeUrlError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {storeUrlError}
                        </p>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      onClick={handleSubmit}
                      className="w-full flex items-center justify-center gap-2 h-10 bg-[var(--accent)] hover:bg-[var(--indigo2)] text-white text-sm font-medium rounded-[6px] transition-colors mt-2"
                    >
                      <Send className="h-4 w-4" />
                      Request demo
                    </button>
                  </motion.div>
                )}

                {/* Loading */}
                {view === "loading" && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center justify-center py-10 gap-4"
                  >
                    {/* <div className="h-10 w-10 rounded-full border-[3px] border-[var(--rule)] border-t-teal-600 animate-spin" /> */}
                    <Loader className="w-20 h-20"/>
                    {/* <p className="text-sm text-gray-600 dark:text-gray-400">
                      Submitting your request
                    </p> */}
                  </motion.div>
                )}

                {/* Success */}
                {view === "success" && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="flex flex-col items-center justify-center py-8 gap-3 text-center"
                  >
                    <div className="h-14 w-14 rounded-full bg-[var(--tint)] dark:bg-[var(--tint)] flex items-center justify-center">
                      <Check className="h-7 w-7 text-[var(--accent2)] dark:text-[var(--accent2)]" />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        Thank you!
                      </p>
                      <p className="text-[12px] text-[var(--mute)] mt-1 max-w-[260px] leading-relaxed">
                        Demo link has been sent to your email.
                      </p>
                    </div>
                    <button
                      onClick={handleClose}
                      className=" px-5 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-[6px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      Close
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookDemoModal;
